import React, { PropsWithChildren, useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";

import { translateMessage } from "src/app/methods/translateMessage";
import useDebounce from "src/hooks/useDebounce";
import { ReactComponent as TickIcon } from "src/images/tick-blue.svg";
import { SelectOption } from "src/types";
import AddButton from "../AddButton/AddButton";
import { DropdownMenuItem } from "../Dropdown/Dropdown";
import { MenuOverlay } from "../Select/components/MenuOverlay/MenuOverlay";
import SelectLabel from "../Select/components/SelectLabel/SelectLabel";
import CustomImage from "../CustomImage/CustomImage";
import { TooltipedEllipsis } from "../TooltipedEllipsis/TooltipedEllipsis";
import { skeletonOptions } from "./utils";

interface SelectDropdownProps {
  options: SelectOption[];
  selectedOptions: SelectOption[];
  handleUserQuery: (query: string) => void;
  handleUnselectOption: (option: SelectOption) => void;
  handleSelectOption: (option: SelectOption) => void;
  handleLabelClick?: () => void;
  isOpen: boolean;
  isLoading?: boolean;
  inputPlaceholder?: {
    id: string;
    defaultMessage: string;
  };
}

export default function SelectDropdown({
  options,
  selectedOptions,
  handleUserQuery,
  handleUnselectOption,
  handleSelectOption,
  handleLabelClick,
  isOpen,
  isLoading,
  inputPlaceholder,
}: PropsWithChildren<SelectDropdownProps>) {
  const [searchValue, setSearchValue] = useState("");

  const inputRef = useRef<HTMLInputElement>(null);
  const debouncedValue = useDebounce(searchValue, 500);

  const intl = useIntl();

  const isOptionSelected = (optionValue: string) =>
    selectedOptions.findIndex(
      (selectedOption) => selectedOption.value === optionValue,
    ) > -1;

  useEffect(() => {
    handleUserQuery(debouncedValue);
  }, [debouncedValue]);

  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
    }
  }, [isOpen]);

  return (
    <MenuOverlay>
      {selectedOptions.length > 0 && (
        <div className="select__selected-labels">
          {selectedOptions.map((selectedOption) => (
            <SelectLabel
              key={selectedOption.value}
              value={selectedOption}
              onClickHandler={handleLabelClick}
              emptyFieldPlaceholder={<AddButton variant="rect" />}
              rejectMulti={handleUnselectOption}
            />
          ))}
        </div>
      )}
      <input
        ref={inputRef}
        className="search-input"
        value={searchValue}
        onChange={(e) => setSearchValue(e.target.value)}
        placeholder={translateMessage({
          intl,
          ...(inputPlaceholder || {
            id: "ws_search",
            defaultMessage: "Search",
          }),
        })}
      />
      <div className="options-list">
        {isLoading
          ? skeletonOptions.map((option) => (
              <DropdownMenuItem key={option.value}>
                {option.label}
              </DropdownMenuItem>
            ))
          : options.map((option) => {
              const showTick = isOptionSelected(option.value);
              return (
                <DropdownMenuItem
                  key={option.value}
                  onClick={() => handleSelectOption(option)}
                >
                  <div className="dropdown__menu-item-left">
                    <span className="select__label">
                      <CustomImage
                        src={option.avatarUrl}
                        className="select__avatar select__avatar--selectable-option"
                        alt="avatar"
                      />
                      <span className="select__label-text-wrapper select__label-text-wrapper--multi">
                        <TooltipedEllipsis
                          className="select__label-text"
                          maxWidth={112}
                          content={option.name}
                        />
                      </span>
                    </span>
                    {showTick && <TickIcon />}
                  </div>
                </DropdownMenuItem>
              );
            })}
      </div>
    </MenuOverlay>
  );
}
